import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { LockerThingTypeMrn } from "shared/lockerData"
import { v4 as uuid } from "uuid"
import { useCreateThingsMutation } from "../../features/things/thingsApi"

/**
 * Delete a locker, behind a confirmation.
 *
 * Extracted whole from `SaLockerPage.tsx` by LOCKER-355 — the page was at 432
 * lines, over the ~400-line cap, and this lane had to add to it. The dialog's
 * three pieces of state and its mutation came with it rather than staying
 * behind as props: they exist only for this dialog, and splitting the markup
 * from the state it owns would have made the page harder to read, not shorter.
 *
 * **No behaviour change of any kind**, including the stale-closure defect in the
 * confirm handler — see the note there. A move that also fixes things is a move
 * nobody can review.
 */
export const SaLockerDeleteDialog: FC<{
  open: boolean
  onClose: () => void
  lockerId?: string
  tenantId?: string
}> = ({ open, onClose, lockerId, tenantId }) => {
  const { t } = useTranslation()
  const [createThings] = useCreateThingsMutation()
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const deleteAction = useCallback(async () => {
    try {
      setIsDeleting(true)
      setDeleteError(null)
      const res = await createThings({
        body: {
          things: [
            {
              thingTypeMrn: ActionReqThingTypeMrn,
              executionMode: ActionReqExecutionModeEnum.sync,
              actionType: ActionReqTypeEnum.DeleteThing,
              idempotencyKey: uuid(),
              tenantId,
              inputData: {
                tenantId,
                id: lockerId,
                thingTypeMrn: LockerThingTypeMrn,
              },
            },
          ],
        },
        params: { tenantId },
      }).unwrap()
      if (res.things?.[0].errors) {
        throw new Error(res.things[0].errors[0].message)
      }
    } catch (error: any) {
      setDeleteError(error.message)
    } finally {
      setIsDeleting(false)
    }
  }, [tenantId, lockerId, createThings])

  return (
    <Dialog data-ui="UI-SL2W-156" open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("confirmDelete")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t("confirmDeleteLockerWarning")}</DialogContentText>
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isDeleting} sx={{ textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <Button
          onClick={async () => {
            // ⚠ CARRIED OVER VERBATIM, INCLUDING A DEFECT. `deleteError` read
            // here is the value from THIS render's closure, so it is still
            // `null` after a failed delete and the page navigates away anyway.
            // Left exactly as it was because LOCKER-355 is a mechanical
            // extraction of this dialog — changing behaviour inside a move is
            // how a move stops being reviewable. Reported on the ticket instead.
            await deleteAction()
            if (!deleteError) {
              onClose()
              window.location.href = "/sa/lockers"
            }
          }}
          variant="contained"
          color="error"
          disabled={isDeleting}
          sx={{ textTransform: "none" }}
        >
          {t("delete")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaLockerDeleteDialog
